import React, { useState } from 'react'
import {
  Button,
  Container,
  FormControl,
  FormLabel,
  Row,
  Spinner,
} from 'react-bootstrap'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import { FaTrash } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import swal from 'sweetalert'
import {
  useGetCreditsQuery,
  useUploadFileMutation,
} from '../../redux/api/brainApi'
import {
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useGetCategoryQuery,
} from '../../redux/api/categoryApi'
import CustomDropzone from '../CustomDropzone'
import { RiCloseLine, RiCopperCoinFill } from 'react-icons/ri'

const EEGUploadForm = () => {
  const [condition, setCondition] = useState('')
  const [mins, setMins] = useState(0)
  const [secs, setSecs] = useState(30)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [file, setFile] = useState(null)

  const counter = 9

  const { userToken, user } = useSelector((state) => state.auth)
  const { selectedUser } = useSelector((state) => state.user)

  const [uploadFile] = useUploadFileMutation()
  const {
    data: credits,
    isLoading: loadingCredits,
    error: creditsError,
  } = useGetCreditsQuery()

  const { data: categories } = useGetCategoryQuery()
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteCategoryMutation()
  const [addCategory, { isLoading: isAdding }] = useAddCategoryMutation()

  const handleCreate = async (inputValue) => {
    await addCategory({ newCategory: inputValue.toLowerCase().trim() })
  }

  const uploadSingle = async (e) => {
    e.preventDefault()
    if (!file) return

    if (
      !condition ||
      mins === undefined ||
      secs === undefined ||
      !counter ||
      !user.headsetName
    ) {
      setError('Fill all fields to upload files')
      setTimeout(() => setError(''), 5000)
      return
    }

    setLoading(true)
    try {
      await uploadFile({
        file,
        selectedUser,
        mins,
        secs,
        counter,
        condition,
        headset: user.headsetName,
        userToken,
      }).unwrap()
      setError('')
      await swal({
        title: 'Success',
        text: 'Uploaded Successfully, Switch to EEG report for updated results',
        icon: 'success',
      })
    } catch (err) {
      setError(err.data ? err.data.error : err.message)
    } finally {
      setLoading(false)
      setFile(null)
    }
  }

  const customStyles = {
    container: (provided, state) => ({
      ...provided,
      fontFamily: 'soraregular',
      width: '100%',
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
    input: (provided, state) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
    placeholder: (provided, state) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
    control: (provided, state) => ({
      ...provided,
      padding: 0,
      border: 'none',
      borderBottom: state.isFocused
        ? '2px solid var(--primary-color)'
        : '2px solid #ebebeb',
      borderRadius: 'none',
      boxShadow: 'none',
      '&:hover': {
        borderBottom: '2px solid #ebebeb',
      },
    }),
    disabled: (provided) => ({
      ...provided,
      borderBottom: '2px solid #ebebeb', // Light gray border color for disabled state
    }),
  }

  const customOption = (props) => {
    const { data } = props

    const handleDelete = async (e) => {
      e.stopPropagation()
      await deleteCategory({ id: data.value })
      setCondition('')
    }

    return (
      <components.Option {...props}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{data.label}</span>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className='border-0 p-0 bg-transparent bg-danger bg-opacity-25'
          >
            <FaTrash className='text-danger' />
          </button>
        </div>
      </components.Option>
    )
  }

  return (
    <Container>
      <Row>
        <Col lg={8} className='dashboard-cards p-4 mx-auto'>
          <div className='d-flex justify-content-end align-items-center gap-1'>
            Credits :
            <span className='fw-bold'>
              <RiCopperCoinFill size={20} />
              {creditsError
                ? creditsError.data.error
                : loadingCredits
                ? '---'
                : credits}
            </span>
          </div>

          <Form onSubmit={uploadSingle}>
            <label>Condition</label>
            <CreatableSelect
              isClearable
              isDisabled={isAdding}
              isLoading={isAdding}
              value={condition}
              onChange={setCondition}
              onCreateOption={handleCreate}
              options={categories}
              styles={customStyles}
              placeholder='Start typing to add options'
              formatCreateLabel={(inputValue) =>
                `Press enter to add '${inputValue}'`
              }
              components={{ Option: customOption }}
              className='mb-3'
            />
            <Row>
              <Col>
                <Form.Group className='mb-3'>
                  <FormLabel htmlFor='mins'>Minutes</FormLabel>
                  <FormControl
                    type='number'
                    name='mins'
                    min={0}
                    max={60}
                    value={mins}
                    onChange={(e) =>
                      setMins(
                        e.target.value > 60
                          ? 60
                          : e.target.value < 0
                          ? 0
                          : e.target.value
                      )
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please enter required field
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='mb-3'>
                  <FormLabel htmlFor='secs'>Seconds</FormLabel>
                  <FormControl
                    type='number'
                    name='secs'
                    min={0}
                    max={60}
                    value={secs}
                    onChange={(e) =>
                      setSecs(
                        e.target.value > 60
                          ? 60
                          : e.target.value < 0
                          ? 0
                          : e.target.value
                      )
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please enter required field
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <FormLabel htmlFor='files'>Files</FormLabel>
              <CustomDropzone
                isMultiple={false}
                onSubmit={(e) => setFile(e)}
                loading={loading}
              />
              <Form.Control.Feedback type='invalid'>
                Please enter required field
              </Form.Control.Feedback>
            </Form.Group>
            {file ? (
              <div
                className='d-flex justify-content-between align-items-center p-3 rounded-3 my-2'
                style={{ backgroundColor: '#f6f6f6' }}
              >
                <div>
                  <p className='fw-bold m-0'>{file.name}</p>
                  <p className='text-secondary m-0'>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  className='border-0 rounded-circle bg-transparent'
                  onClick={() => setFile(null)}
                  disabled={loading}
                >
                  <RiCloseLine size={24} />
                </button>
              </div>
            ) : null}
            {error && <p className='text-danger'>{error}</p>}

            <Button
              type='submit'
              className='border-0 common-btn w-100 py-3 mt-2'
              disabled={loading}
            >
              {loading ? (
                <div className='d-flex align-items-center justify-content-center gap-2'>
                  <Spinner animation='border' />
                  Uploading...
                </div>
              ) : (
                'Upload'
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  )
}

export default EEGUploadForm
